import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";
import {
  armBoardAudioElement,
  isArmingBoardAudio,
  unlockBoardAudio,
  whenBoardAudioUnlocked,
} from "../../../lib/audio/boardAudioUnlock";

/**
 * Аудио и голосовые рисуются обычным HTML в слое доски, а не внутри SVG:
 * Safari не применяет к HTML внутри SVG место объекта, и карточка вместе
 * с кнопкой воспроизведения уезжала в угол видимой части доски.
 * См. editor/render/htmlLayerObject.
 */

export type AudioAction =
  | { type: "setListenTogether"; listenTogether: boolean }
  | { type: "playback"; playing: boolean; position: number };

export type AudioRenderOpts = {
  assetContentUrl: (assetId: string) => string;
  useCredentials?: boolean;
  canEdit?: boolean;
  onAudioAction?: (objectId: string, action: AudioAction) => void;
};

type AudioCtl = {
  applying: boolean;
  lastSig: string;
  emitTimer: number;
  /** Последний объект для retry после unlock. */
  lastObj: SceneObject | null;
};

function audioCtl(el: HTMLAudioElement): AudioCtl {
  const anyEl = el as HTMLAudioElement & { __wbAudio?: AudioCtl };
  if (!anyEl.__wbAudio) {
    anyEl.__wbAudio = { applying: false, lastSig: "", emitTimer: 0, lastObj: null };
  }
  return anyEl.__wbAudio;
}

function playbackSig(obj: SceneObject): string {
  return [
    obj.listenTogether ? "1" : "0",
    obj.audioPlaying ? "1" : "0",
    String(obj.audioStartedAt ?? ""),
    String(Math.round((Number(obj.audioPosition) || 0) * 10)),
  ].join("|");
}

function targetTime(obj: SceneObject): number {
  const pos = Math.max(0, Number(obj.audioPosition) || 0);
  if (!obj.audioPlaying || typeof obj.audioStartedAt !== "number") return pos;
  return pos + Math.max(0, (Date.now() - obj.audioStartedAt) / 1000);
}

function seekTo(audio: HTMLAudioElement, t: number) {
  try {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      audio.currentTime = Math.min(audio.duration, t);
    } else {
      audio.currentTime = t;
    }
  } catch {
    /* not ready */
  }
}

function applySharedPlayback(audio: HTMLAudioElement, obj: SceneObject) {
  const ctl = audioCtl(audio);
  ctl.lastObj = obj;
  const sig = playbackSig(obj);
  if (!obj.listenTogether) {
    if (ctl.lastSig.startsWith("1|") && !audio.paused) {
      ctl.applying = true;
      audio.pause();
      window.setTimeout(() => {
        ctl.applying = false;
      }, 80);
    }
    ctl.lastSig = sig;
    return;
  }
  if (sig === ctl.lastSig) return;
  ctl.lastSig = sig;
  ctl.applying = true;
  const wantPlay = Boolean(obj.audioPlaying);
  const t = targetTime(obj);
  const closeEnough = Math.abs((audio.currentTime || 0) - t) < 0.55;
  if (!closeEnough) seekTo(audio, t);

  const finishApplying = () => {
    window.setTimeout(() => {
      ctl.applying = false;
    }, 80);
  };

  if (wantPlay && audio.paused) {
    void audio
      .play()
      .then(finishApplying)
      .catch(() => {
        // Autoplay: ждём жест на доске, затем догоняем позицию и play.
        ctl.lastSig = "";
        finishApplying();
        whenBoardAudioUnlocked(() => {
          const latest = ctl.lastObj;
          if (!latest?.listenTogether || !latest.audioPlaying) return;
          ctl.applying = true;
          seekTo(audio, targetTime(latest));
          void audio
            .play()
            .catch(() => undefined)
            .finally(finishApplying);
        });
      });
    return;
  }
  if (!wantPlay && !audio.paused) {
    audio.pause();
  }
  finishApplying();
}

function bindSharedPlayback(
  audio: HTMLAudioElement,
  objectId: string,
  opts: AudioRenderOpts
) {
  const emit = (playing: boolean) => {
    const ctl = audioCtl(audio);
    if (ctl.applying || !opts.onAudioAction) return;
    if (isArmingBoardAudio(audio)) return;
    const card = audio.closest(".wb-scene-obj__inner--audio");
    const box = card?.querySelector(".wb-audio-card__together") as HTMLInputElement | null;
    // Достаточно общей галочки на объекте (сцена) — не локальный «opt-in» каждого.
    const together =
      box?.checked || card?.getAttribute("data-listen-together") === "1";
    if (!together) return;
    window.clearTimeout(ctl.emitTimer);
    ctl.emitTimer = window.setTimeout(() => {
      opts.onAudioAction?.(objectId, {
        type: "playback",
        playing,
        position: audio.currentTime || 0,
      });
    }, 40);
  };
  audio.addEventListener("play", () => {
    if (isArmingBoardAudio(audio)) return;
    unlockBoardAudio();
    emit(true);
  });
  audio.addEventListener("pause", () => {
    if (isArmingBoardAudio(audio)) return;
    emit(false);
  });
  audio.addEventListener("seeked", () => {
    if (isArmingBoardAudio(audio)) return;
    emit(!audio.paused);
  });
  audio.addEventListener("ended", () => emit(false));
}

export function syncAudioObjectOnGroup(
  g: HTMLElement,
  obj: SceneObject,
  opts: AudioRenderOpts
): boolean {
  if (!obj.assetId) return false;
  if (!g.querySelector(".wb-audio-card__together")) return false;
  const w = typeof obj.w === "number" ? obj.w : 280;
  const h = typeof obj.h === "number" ? obj.h : 72;
  placeHtmlObject(g, obj, w, h);
  const titleEl = g.querySelector(".wb-audio-card__title");
  const title =
    typeof obj.title === "string" && obj.title.trim() ? obj.title.trim() : "Аудио";
  if (titleEl) titleEl.textContent = title;

  const card = g.querySelector(".wb-audio-card") as HTMLElement | null;
  if (card) {
    card.setAttribute("data-listen-together", obj.listenTogether ? "1" : "0");
  }

  const box = g.querySelector(".wb-audio-card__together") as HTMLInputElement | null;
  if (box) {
    box.checked = Boolean(obj.listenTogether);
    box.disabled = !opts.canEdit;
  }

  const audio = g.querySelector("audio") as HTMLAudioElement | null;
  if (audio) applySharedPlayback(audio, obj);
  return true;
}

export function renderAudioObject(
  obj: SceneObject,
  opts: AudioRenderOpts | ((assetId: string) => string)
): HTMLElement | null {
  const resolved: AudioRenderOpts =
    typeof opts === "function" ? { assetContentUrl: opts, useCredentials: true } : opts;
  if (!obj.assetId) return null;
  const assetId = String(obj.assetId);
  const w = typeof obj.w === "number" ? obj.w : 280;
  const h = typeof obj.h === "number" ? obj.h : 72;
  const title =
    typeof obj.title === "string" && obj.title.trim() ? obj.title.trim() : "Аудио";
  const canEdit = Boolean(resolved.canEdit);

  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--audio");
  g.setAttribute("data-object-id", obj.id);
  placeHtmlObject(g, obj, w, h);

  const div = document.createElement("div");
  div.setAttribute("class", "wb-scene-obj__inner wb-scene-obj__inner--audio wb-audio-card");
  div.setAttribute("data-listen-together", obj.listenTogether ? "1" : "0");

  const head = document.createElement("div") as HTMLDivElement;
  head.className = "wb-audio-card__head";

  const label = document.createElement("div");
  label.setAttribute("class", "wb-audio-card__title");
  label.textContent = title;
  head.appendChild(label);

  const together = document.createElement("label") as HTMLLabelElement;
  together.className = "wb-audio-card__together-label";
  together.title =
    "Достаточно одному включить: play/pause синхронизируется для всех на доске";
  together.addEventListener("pointerdown", (ev) => ev.stopPropagation());
  together.addEventListener("mousedown", (ev) => ev.stopPropagation());
  together.addEventListener("click", (ev) => ev.stopPropagation());
  const box = document.createElement("input") as HTMLInputElement;
  box.type = "checkbox";
  box.className = "wb-audio-card__together";
  box.checked = Boolean(obj.listenTogether);
  box.disabled = !canEdit;
  box.addEventListener("pointerdown", (ev) => ev.stopPropagation());
  box.addEventListener("mousedown", (ev) => ev.stopPropagation());
  box.addEventListener("click", (ev) => ev.stopPropagation());
  if (canEdit) {
    box.addEventListener("change", (ev) => {
      ev.stopPropagation();
      unlockBoardAudio();
      div.setAttribute("data-listen-together", box.checked ? "1" : "0");
      resolved.onAudioAction?.(obj.id, {
        type: "setListenTogether",
        listenTogether: box.checked,
      });
    });
  }
  together.appendChild(box);
  together.appendChild(document.createTextNode(" слушать вместе"));
  head.appendChild(together);
  div.appendChild(head);

  const audio = document.createElement("audio") as HTMLAudioElement;
  audio.setAttribute("class", "wb-audio-card__player");
  audio.controls = true;
  audio.preload = "metadata";
  audio.setAttribute("controls", "controls");
  audio.setAttribute("preload", "metadata");
  audio.addEventListener("pointerdown", (ev) => ev.stopPropagation());
  audio.addEventListener("mousedown", (ev) => ev.stopPropagation());
  audio.addEventListener("click", (ev) => ev.stopPropagation());
  bindSharedPlayback(audio, obj.id, resolved);
  armBoardAudioElement(audio);
  div.appendChild(audio);

  const status = document.createElement("div");
  status.setAttribute("class", "wb-audio-card__status");
  status.textContent = "Загрузка…";
  div.appendChild(status);

  const contentUrl = resolved.assetContentUrl(assetId);
  audio.addEventListener(
    "loadedmetadata",
    () => {
      status.textContent = "";
      status.style.display = "none";
      applySharedPlayback(audio, obj);
    },
    { once: true }
  );
  audio.addEventListener(
    "error",
    () => {
      status.textContent = "Не удалось загрузить аудио";
      status.style.display = "";
    },
    { once: true }
  );
  audio.src = contentUrl;

  // Карточка занимает объект целиком: рамку задаёт внешний узел.
  div.style.width = "100%";
  div.style.height = "100%";
  div.style.boxSizing = "border-box";
  // Содержимое прижато к верху. В Safari проигрыватель выше своего собрата
  // в Chrome (31 px против 21), и при выравнивании по центру он выдавливал
  // название за верхний край карточки — оставалась одна кнопка, будто
  // «уехавшая». Снизу в этом случае прячется только строка состояния.
  div.style.justifyContent = "flex-start";
  g.appendChild(div);
  return g;
}
